import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, MapPin, Instagram, Users, Sparkles, ArrowRight, GraduationCap, School, Mail, Tag } from "lucide-react";
import { api, COLORS } from "@/lib/api";
import { MEXICO_VIEWBOX, STATE_PATHS } from "@/data/mexicoStates";
import { STATE_LABEL } from "@/data/clubs";

const GRAY = "#E6E6E6";
const LIGHT = [251, 208, 243]; // magenta pálido
const STRONG = [255, 0, 221]; // COLORS.magenta

const mix = (a, b, t) => a.map((v, i) => Math.round(v + (b[i] - v) * t));
const fillFor = (count, max) => {
  if (!count) return GRAY;
  const ratio = Math.sqrt(count / (max || 1));
  const [r, g, b] = mix(LIGHT, STRONG, ratio);
  return `rgb(${r},${g},${b})`;
};

const label = (name) => STATE_LABEL[name] || name;

const Legend = ({ max }) => {
  const steps = [1, Math.round(max / 3), Math.round((2 * max) / 3), max].filter((v, i, a) => v > 0 && a.indexOf(v) === i);
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="map-legend">
      <span className="text-xs font-semibold text-brand-ink/60 uppercase tracking-wide">Menos</span>
      <div className="flex items-center">
        {steps.map((s) => (
          <span key={s} className="w-8 h-4" style={{ background: fillFor(s, max) }} />
        ))}
      </div>
      <span className="text-xs font-semibold text-brand-ink/60 uppercase tracking-wide">Más clubs</span>
      <span className="flex items-center gap-1.5 ml-2 text-xs text-brand-ink/50">
        <span className="w-4 h-4 inline-block" style={{ background: GRAY }} /> Sin clubs aún
      </span>
    </div>
  );
};

const igUser = (url) => {
  const m = url.replace(/\/+$/, "").split("/");
  return "@" + m[m.length - 1];
};

const ClubItem = ({ club }) => (
  <li data-testid="club-item" className="py-3 border-b border-[#EEE] last:border-0">
    <div className="flex items-center gap-2 flex-wrap">
      <p className="font-semibold text-brand-ink leading-tight">{club.nombre}</p>
      {club.giro && club.giro !== "General" && (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#FDE7FA", color: COLORS.magenta }}>
          <Tag size={10} /> {club.giro}
        </span>
      )}
    </div>
    <p className="text-sm text-brand-ink/55 flex items-center gap-1 mt-0.5 flex-wrap">
      {club.nivel ? (
        <span className="inline-flex items-center gap-1">
          <GraduationCap size={12} /> {club.nivel}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1">
          <MapPin size={12} /> Club de la red
        </span>
      )}
    </p>
    {club.institucion && (
      <p className="text-xs text-brand-ink/55 flex items-start gap-1 mt-1">
        <School size={12} className="flex-shrink-0 mt-0.5" /> {club.institucion}
      </p>
    )}
    <div className="mt-2 flex flex-col gap-1">
      {club.instagram && (
        <a
          href={club.instagram}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="club-instagram"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-magenta hover:underline w-fit break-all"
        >
          <Instagram size={14} className="flex-shrink-0" /> {igUser(club.instagram)}
        </a>
      )}
      {club.email && (
        <a
          href={`mailto:${club.email}`}
          data-testid="club-email"
          className="inline-flex items-center gap-1.5 text-sm text-brand-ink/70 hover:text-brand-ink hover:underline w-fit break-all"
        >
          <Mail size={14} className="flex-shrink-0" /> {club.email}
        </a>
      )}
    </div>
  </li>
);

const DirectorioClubs = () => {
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [byState, setByState] = useState({});

  useEffect(() => {
    api.get("/clubs").then((r) => {
      const grouped = {};
      r.data.forEach((c) => {
        (grouped[c.estado] = grouped[c.estado] || []).push(c);
      });
      setByState(grouped);
    }).catch(() => {});
  }, []);

  const getCount = (name) => (byState[name] || []).length;
  const totalClubs = Object.values(byState).reduce((a, c) => a + c.length, 0);
  const totalStates = Object.keys(byState).length;
  const maxClubs = Math.max(1, ...Object.values(byState).map((c) => c.length));
  const clubs = selected ? byState[selected] || [] : [];

  return (
    <div data-testid="subpage-directorio-de-clubs">
      {/* Hero */}
      <section
        className="relative overflow-hidden border-b border-[#E5E5E5]"
        style={{ background: `linear-gradient(120deg, ${COLORS.magenta} 0%, ${COLORS.orange} 100%)` }}
      >
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <Link to="/red-de-clubs" data-testid="subpage-back" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white mb-6">
            <ArrowLeft size={16} /> Red de Clubs
          </Link>
          <span className="inline-block w-16 h-1.5 rounded-sm mb-6 bg-white" />
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[0.98]">
            Directorio de Clubs
          </h1>
          <p className="mt-5 text-lg md:text-xl text-white/90 font-medium max-w-2xl">
            Explora nuestra red en el mapa. Haz clic en un estado para conocer sus clubs activos.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-14">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
          {[
            { n: totalClubs, l: "Clubs activos", c: COLORS.magenta },
            { n: totalStates, l: "Estados con presencia", c: COLORS.orange },
            { n: "13-24", l: "Rango de edad", c: COLORS.lime },
          ].map((s) => (
            <div key={s.l} className="border border-[#E5E5E5] rounded-sm p-5">
              <span className="inline-block w-8 h-1 rounded-sm mb-3" style={{ background: s.c }} />
              <p className="font-heading text-3xl md:text-4xl font-bold text-brand-ink">{s.n}</p>
              <p className="text-sm text-brand-ink/60 mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Mapa */}
          <div className="lg:col-span-3">
            <div className="relative border border-[#E5E5E5] rounded-sm p-3 bg-white">
              <svg viewBox={MEXICO_VIEWBOX} className="w-full h-auto" data-testid="directorio-map" role="img" aria-label="Mapa de clubs por estado">
                {STATE_PATHS.map((st) => {
                  const count = getCount(st.name);
                  const isSel = selected === st.name;
                  const isHov = hovered === st.name;
                  return (
                    <path
                      key={st.id}
                      d={st.d}
                      data-testid={`state-${st.id}`}
                      onClick={() => setSelected(st.name)}
                      onMouseEnter={() => setHovered(st.name)}
                      onMouseLeave={() => setHovered(null)}
                      style={{ cursor: "pointer", transition: "fill 0.2s, stroke 0.2s" }}
                      fill={isHov && count ? "#FF33E3" : fillFor(count, maxClubs)}
                      stroke={isSel ? COLORS.orange : "#FFFFFF"}
                      strokeWidth={isSel ? 2.4 : 0.8}
                    >
                      <title>{`${label(st.name)}: ${count} ${count === 1 ? "club" : "clubs"}`}</title>
                    </path>
                  );
                })}
              </svg>
              {hovered && (
                <div className="absolute top-3 left-3 bg-brand-ink text-white text-sm font-semibold px-3 py-1.5 rounded-sm pointer-events-none" data-testid="map-tooltip">
                  {label(hovered)} · {getCount(hovered)} {getCount(hovered) === 1 ? "club" : "clubs"}
                </div>
              )}
            </div>
            <div className="mt-4">
              <Legend max={maxClubs} />
            </div>
          </div>

          {/* Panel de info */}
          <div className="lg:col-span-2 lg:sticky lg:top-24">
            <AnimatePresence mode="wait">
              {!selected ? (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="border border-dashed border-[#D8D8D8] rounded-sm p-8 text-center"
                  data-testid="state-panel-empty"
                >
                  <Sparkles size={26} className="text-brand-magenta mx-auto" />
                  <p className="mt-3 font-heading text-xl font-semibold text-brand-ink">Selecciona un estado</p>
                  <p className="mt-2 text-sm text-brand-ink/60">Haz clic en cualquier estado del mapa para ver los clubs activos y sus redes sociales.</p>
                </motion.div>
              ) : (
                <motion.div
                  key={selected}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="border border-[#E5E5E5] rounded-sm overflow-hidden"
                  data-testid="state-panel"
                >
                  <div className="p-5" style={{ background: fillFor(getCount(selected), maxClubs) }}>
                    <p className="font-heading text-2xl font-bold text-brand-ink">{label(selected)}</p>
                    <p className="text-sm font-semibold text-brand-ink/70 flex items-center gap-1.5 mt-1">
                      <Users size={14} /> {clubs.length} {clubs.length === 1 ? "club activo" : "clubs activos"}
                    </p>
                  </div>
                  <div className="p-5">
                    {clubs.length > 0 ? (
                      <ul>
                        {clubs.map((c) => (
                          <ClubItem key={c.id || c.nombre} club={c} />
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-brand-ink/70">Aún no hay clubs en este estado.</p>
                        <Link to="/red-de-clubs/como-abrir-un-club" className="mt-3 inline-flex items-center gap-2 font-semibold text-brand-magenta hover:underline">
                          ¿Abres el primero? <ArrowRight size={15} />
                        </Link>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* CTA */}
        <Link to="/red-de-clubs/como-abrir-un-club" data-testid="abrir-club-cta" className="mt-14 group flex items-center justify-between gap-4 bg-brand-ink rounded-sm p-8 hover:bg-brand-magenta transition-colors duration-200">
          <div>
            <h3 className="font-heading text-2xl font-semibold tracking-tight text-white">¿No hay un club en tu estado?</h3>
            <p className="mt-1 text-white/70">Abre el tuyo y súmate a la red de Juventudes Latentes.</p>
          </div>
          <ArrowRight size={26} className="text-white flex-shrink-0 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </div>
  );
};

export default DirectorioClubs;
